import { useCallback, useContext, useEffect, useRef } from 'react';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import {
	type SetBatchedTaskStatusVariables,
	type SetBatchedTaskStatus,
	type InlineTask,
	TaskStatus,
	PageUpdateTrigger,
} from '@confluence/fabric-media-support/entry-points/SetBatchedTaskStatusMutation.ts';
import {
	TaskBatcher,
	SetBatchedTaskStatusMutation,
} from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { SET_TASK_COMPLETE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const TASK_UPDATE_INTERVAL_MS_CONFIG = 'confluence_task_update_batch_interval_ms';
const DEFAULT_TASK_UPDATE_INTERVAL_MS = 500;

export const getBatchesByContentIds = (taskActions: TaskAction[]) =>
	taskActions.reduce(
		(batches, taskAction) => {
			const { objectKey, state, contentId } = taskAction;

			if (!contentId) {
				return batches;
			}

			// Format the task from the batch
			const task: InlineTask = {
				taskId: objectKey.localId,
				status: state === 'DONE' ? TaskStatus.CHECKED : TaskStatus.UNCHECKED,
			};

			// Save batch to record for later processing.
			return {
				...batches,
				[contentId]: {
					tasks: [...(batches?.[contentId]?.tasks ?? []), task],
					trigger: PageUpdateTrigger.VIEW_PAGE,
					contentId,
				},
			};
		},
		{} as Record<string, SetBatchedTaskStatusVariables>,
	);

export const useTaskBatcher = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [mutateFunction, { data, loading, error }] = useMutationInterop<
		SetBatchedTaskStatus,
		SetBatchedTaskStatusVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SetBatchedTaskStatusMutation,
	);

	type MutateResponse = ReturnType<typeof mutateFunction>;

	const taskBatcherRef = useRef(
		new TaskBatcher(
			StatsigConfigurations.getNumber(
				TASK_UPDATE_INTERVAL_MS_CONFIG,
				DEFAULT_TASK_UPDATE_INTERVAL_MS,
			),
		),
	); // Handlers for batched task update. Batches with contentId.
	const handleBatchTaskUpdate = useCallback(
		async (taskActions: TaskAction[]) => {
			experienceTracker.start({ name: SET_TASK_COMPLETE_EXPERIENCE });

			const updateBatches = getBatchesByContentIds(taskActions);

			// Run batches by contentId due to graph limitation.
			const queries: MutateResponse[] = Object.values(updateBatches).map(async (batch) => {
				const result = await mutateFunction({
					variables: batch,
				});

				return result;
			});

			const responses = await Promise.allSettled(queries);

			const errors = responses.reduce((flattenedErrors, response) => {
				if (response.status === 'rejected') {
					return [...flattenedErrors, new Error(response.reason)];
				}

				return flattenedErrors;
			}, [] as Error[]);

			if (errors.length === 0) {
				experienceTracker.succeed({
					name: SET_TASK_COMPLETE_EXPERIENCE,
					attributes: {
						batches: JSON.stringify(updateBatches),
					},
				});
			} else {
				experienceTracker.fail({
					name: SET_TASK_COMPLETE_EXPERIENCE,
					error: new Error(`Could not complete task complete mutation: ${JSON.stringify(errors)}`),
				});
			}
		},
		[mutateFunction, experienceTracker],
	);

	useEffect(() => {
		taskBatcherRef.current.onFlush(handleBatchTaskUpdate);
	}, [taskBatcherRef, handleBatchTaskUpdate]);

	return {
		add: taskBatcherRef.current.add,
		data,
		loading,
		error,
	};
};
